import { NodeTriggerType, NodeType } from "Types/Journey";

import { getConditionalNodeBody, isConditionalNodeConfigured } from "./conditionalNodePresentation";
import { parseTriggerNodeData } from "./triggerNodeData";

function isPluralUnit(value: string | number): string {
    return Number(value) === 1 ? "" : "s";
}

function getPropertyDelayLabel(data: Record<string, unknown>): string {
    const property = (data.delayPropertyName || data.contactAttribute) as string | undefined;
    if (!property) {
        return "";
    }

    const hasOffset = Boolean(data.days || data.hours || data.minutes || data.seconds);
    if (!hasOffset) {
        return property;
    }

    const offsetText = data.delayOffsetType === "plus" ? "After " : "Before ";
    return `${offsetText}${property}`;
}

/** Format delay node body text (duration and/or property-based wait). */
export function formatDelayDuration(data: Record<string, unknown> | undefined): string {
    if (!data) {
        return "Set delay";
    }
    if (data.empty) {
        return "Delay";
    }

    const activePropertyDelay =
        data.delayType === "property" && Boolean(data.contactAttribute || data.delayPropertyName);

    const parts: string[] = [];
    const units: Array<[string, string]> = [
        ["days", "Day"],
        ["hours", "Hour"],
        ["minutes", "Minute"],
        ["seconds", "Second"],
    ];

    for (const [key, label] of units) {
        const value = data[key];
        if (value && value !== "0" && value !== 0) {
            parts.push(`${value} ${label}${isPluralUnit(value as string | number)}`);
        }
    }

    if (parts.length === 0) {
        if (activePropertyDelay) {
            return `Wait Until ${getPropertyDelayLabel(data)}`;
        }
        return "Set delay";
    }

    const timing = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
    const propertySuffix = data.delayType === "property" ? ` ${getPropertyDelayLabel(data)}` : "";
    const until = data.delayType === "property" ? " Until" : "";
    return `Wait${until} ${timing}${propertySuffix}`.trim();
}

function getCampaignName(data: Record<string, unknown> | undefined): string | undefined {
    const campaign = data?.campaign;
    if (campaign && typeof campaign === "object") {
        const record = campaign as Record<string, unknown>;
        if (typeof record.name === "string" && record.name) {
            return record.name;
        }
        if (typeof record.title === "string" && record.title) {
            return record.title;
        }
    }
    return undefined;
}

function getTriggerDisplayText(parsed: {
    eventType?: string;
    triggerType?: string;
    eventName?: string;
    segmentName?: string;
}): string | undefined {
    const eventType = parsed.eventType ?? parsed.triggerType;
    const eventName = parsed.eventName;
    const segmentName = parsed.segmentName;

    if (!eventType && !eventName && !segmentName) {
        return undefined;
    }

    switch (eventType) {
        case NodeTriggerType.AddContactToStaticSegment:
            return "Contact Added to Static Segment";
        case NodeTriggerType.Pageview:
            return "Pageview";
        case NodeTriggerType.Scheduled:
            return segmentName ? `Scheduled from ${segmentName}` : "Scheduled";
        default:
            return eventName ?? eventType;
    }
}

function getTriggerSummary(data: Record<string, unknown> | undefined): string {
    if (!data || data.empty) {
        return "Select a trigger";
    }

    const parsed = parseTriggerNodeData({ data: data as Record<string, unknown> });
    return getTriggerDisplayText(parsed) ?? "Select a trigger";
}

function getVariantSummary(data: Record<string, unknown> | undefined, fallback: string): string {
    const testName = data?.testName;
    if (typeof testName === "string" && testName) {
        return testName;
    }
    const name = data?.name;
    if (typeof name === "string" && name) {
        return name;
    }
    const variants = data?.variants;
    if (variants && typeof variants === "object") {
        const names = Object.values(variants as Record<string, unknown>)
            .map((variant) => {
                if (variant && typeof variant === "object") {
                    const record = variant as Record<string, unknown>;
                    return typeof record.name === "string" ? record.name : undefined;
                }
                return undefined;
            })
            .filter(Boolean);
        if (names.length > 0) {
            return names.join(", ");
        }
    }
    return fallback;
}

function getCancelTaskSummary(data: Record<string, unknown> | undefined): string {
    if (!data || data.empty) {
        return "Cancel Task";
    }

    const campaign = data.campaign;
    if (campaign && typeof campaign === "object") {
        const record = campaign as Record<string, unknown>;
        const friendlyId = record.friendlyId;
        const name = record.name;
        if (friendlyId !== undefined || typeof name === "string") {
            return `#${friendlyId ?? ""} ${typeof name === "string" ? name : ""}`.trim();
        }
    }

    const taskType = data.taskType;
    const cancelType = data.cancelType;

    if (taskType === "Custom Task") {
        return "Custom Tasks";
    }

    if (cancelType === "allTasks") {
        return "All Tasks";
    }

    if (cancelType === "taskType") {
        return `${taskType} Tasks`;
    }

    return "Select cancellation criteria";
}

function getAssignTargetAgentSummary(data: Record<string, unknown> | undefined): string {
    if (!data || data.empty) {
        return "Assign Agent";
    }

    const targetAgent = data.targetAgent;
    if (!targetAgent || typeof targetAgent !== "object") {
        return "Select Target Agent";
    }

    const record = targetAgent as Record<string, unknown>;
    if (!record.key) {
        return "Select Target Agent";
    }

    return typeof record.name === "string" ? record.name : "Select Target Agent";
}

function getUpdateTaskSummary(data: Record<string, unknown> | undefined): string {
    if (!data || data.empty) {
        return "Update Task";
    }

    if (typeof data.taskType === "string" && data.taskType) {
        return data.taskType;
    }

    if (typeof data.campaign === "string" && data.campaign) {
        return data.campaign;
    }

    if (data.campaign && typeof data.campaign === "object") {
        const record = data.campaign as Record<string, unknown>;
        if (typeof record.name === "string" && record.name) {
            return record.name;
        }
    }

    if (typeof data.updateCriteria === "string" && data.updateCriteria) {
        return data.updateCriteria;
    }

    return "Select update criteria";
}

/** One-line body summary for a journey node (builder + Copilot preview). */
export function getJourneyNodeContentSummary(
    nodeType: NodeType | undefined,
    data: Record<string, unknown> | undefined
): string {
    switch (nodeType) {
        case NodeType.Trigger:
            return getTriggerSummary(data);
        case NodeType.Delay:
            return formatDelayDuration(data);
        case NodeType.Call:
        case NodeType.SMS:
        case NodeType.AgentSMSTask: {
            const campaignName = getCampaignName(data);
            return campaignName ?? (data?.empty ? "Action" : "Select campaign");
        }
        case NodeType.CustomTask: {
            if (!data || data.empty) {
                return data?.empty ? "Create Custom Task" : "Set custom task";
            }
            const task = data.task;
            if (task && typeof task === "object") {
                const name = (task as Record<string, unknown>).name;
                if (typeof name === "string" && name) {
                    return name;
                }
            }
            return "Set custom task";
        }
        case NodeType.Conditional:
            return getConditionalNodeBody(data);
        case NodeType.ABTest: {
            const isActive = Boolean(data?.testName || data?.empty);
            return isActive
                ? typeof data?.testName === "string" && data.testName
                    ? data.testName
                    : "A/B Test"
                : "Set variants";
        }
        case NodeType.DecisionAI:
            return getVariantSummary(data, "Set decision paths");
        case NodeType.Webhook:
            if (data?.empty) {
                return "Webhook";
            }
            if (typeof data?.webhookName === "string" && data.webhookName) {
                return data.webhookName;
            }
            if (typeof data?.url === "string" && data.url) {
                return data.url;
            }
            if (typeof data?.webhookUrl === "string" && data.webhookUrl) {
                return data.webhookUrl;
            }
            return "Configure webhook";
        case NodeType.StickyNote:
            return typeof data?.text === "string" && data.text ? data.text : "Note";
        case NodeType.UpdateContact:
            return typeof data?.propertyName === "string" ? data.propertyName : "Update contact property";
        case NodeType.CancelTask:
            return getCancelTaskSummary(data);
        case NodeType.AssignTargetAgent:
            return getAssignTargetAgentSummary(data);
        case NodeType.UpdateTask:
            return getUpdateTaskSummary(data);
        case NodeType.Reminder:
            return "Reminder";
        case NodeType.ScheduledCallback:
            return "Scheduled Callback";
        default:
            return data?.empty ? "Configure node" : "Configured";
    }
}

/** Whether a node has enough data to show as configured (preview active chrome). */
export function isJourneyNodeConfigured(
    nodeType: NodeType | undefined,
    data: Record<string, unknown> | undefined
): boolean {
    if (!data) {
        return false;
    }
    if (data.empty) {
        return true;
    }

    switch (nodeType) {
        case NodeType.Trigger: {
            const parsed = parseTriggerNodeData({ data: data as Record<string, unknown> });
            return Boolean(getTriggerDisplayText(parsed));
        }
        case NodeType.Delay:
            return Boolean(
                data.days ||
                    data.hours ||
                    data.minutes ||
                    data.seconds ||
                    data.delayPropertyName ||
                    data.contactAttribute
            );
        case NodeType.Call:
        case NodeType.SMS:
        case NodeType.AgentSMSTask:
            return Boolean(getCampaignName(data));
        case NodeType.CustomTask: {
            const task = data.task;
            if (task && typeof task === "object") {
                return Boolean((task as Record<string, unknown>).name);
            }
            return false;
        }
        case NodeType.Conditional:
            return isConditionalNodeConfigured(data);
        case NodeType.ABTest:
        case NodeType.DecisionAI:
            return Boolean(data.variants || data.testName || data.name);
        case NodeType.Webhook:
            return Boolean(data.url || data.webhookUrl || data.webhookName);
        case NodeType.StickyNote:
            return Boolean(data.text);
        default:
            return Object.keys(data).length > 0;
    }
}
